import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  FormModal,
  Input,
  TextArea,
} from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import SubNavigation from '../../components/admin/SubNavigation';
import {
  Search,
  Close,
  MonetizationOn,
  Payments,
  CheckCircle,
  Warning,
  TrendingUp,
  Person,
  LocalOffer,
  CalendarMonth,
  Receipt,
  PersonAdd
} from '../../mui-icons';
import './CommissionsPage.css';

const CommissionsPage = () => {
  const { canEdit } = useAdminAuth();
  
  // Dashboard State
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  
  // List State
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [influencerFilter, setInfluencerFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [totals, setTotals] = useState(null);
  
  // Influencers for filter
  const [influencers, setInfluencers] = useState([]);
  
  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Modal States
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState('');
  
  // Bulk Pay Modal
  const [showBulkPayModal, setShowBulkPayModal] = useState(false);
  const [bulkPayLoading, setBulkPayLoading] = useState(false);

  // Load dashboard
  const loadDashboard = async () => {
    try {
      const response = await adminAPI.getCommissionsDashboard();
      if (response.status === 'success') {
        setDashboard(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Load influencers for filter
  useEffect(() => {
    const loadInfluencers = async () => {
      try {
        const response = await adminAPI.getInfluencers({ limit: 100, active_only: 'true' });
        if (response.status === 'success') {
          setInfluencers(response.data.items || []);
        }
      } catch (err) {
        console.error('Erro ao carregar influenciadores:', err);
      }
    };
    loadInfluencers();
    loadDashboard();
  }, []);

  // Load commissions
  const loadCommissions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(influencerFilter && { idinfluencer: influencerFilter }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo })
      };

      const response = await adminAPI.getCommissions(params);
      
      if (response.status === 'success') {
        setCommissions(response.data.items || []);
        setTotals(response.data.totals || null);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          total_pages: response.data.pagination?.total_pages || 0
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar comissões:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, statusFilter, influencerFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadCommissions();
  }, [loadCommissions]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    setSelectedIds([]);
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const pendingIds = commissions
        .filter(c => c.status === 'pending')
        .map(c => c.idinfluencercommissionpayment);
      setSelectedIds(pendingIds);
    } else {
      setSelectedIds([]);
    }
  };

  // Handle select one
  const handleSelectOne = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      }
      return [...prev, id];
    });
  };

  // Open pay modal
  const openPayModal = (commission) => {
    setSelectedCommission(commission);
    setPaymentReference('');
    setPaymentNotes('');
    setPayError('');
    setShowPayModal(true);
  };

  // Handle single pay
  const handlePay = async () => {
    setPayLoading(true);
    setPayError('');

    try {
      await adminAPI.payCommission(selectedCommission.idinfluencercommissionpayment, {
        payment_reference: paymentReference,
        notes: paymentNotes
      });
      setShowPayModal(false);
      loadCommissions();
      loadDashboard();
    } catch (err) {
      setPayError(err.message);
    } finally {
      setPayLoading(false);
    }
  };

  // Handle bulk pay
  const handleBulkPay = async () => {
    setBulkPayLoading(true);
    setPayError('');

    try {
      await adminAPI.bulkPayCommissions(selectedIds, {
        payment_reference: paymentReference,
        notes: paymentNotes
      });
      setShowBulkPayModal(false);
      setSelectedIds([]);
      loadCommissions();
      loadDashboard();
    } catch (err) {
      setPayError(err.message);
    } finally {
      setBulkPayLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendente', class: 'status-warning' },
      paid: { label: 'Pago', class: 'status-success' },
      cancelled: { label: 'Cancelado', class: 'status-error' }
    };
    const { label, class: className } = statusMap[status] || { label: status, class: '' };
    return <span className={`status-badge ${className}`}>{label}</span>;
  };

  // Calculate selected total
  const selectedTotal = commissions
    .filter(c => selectedIds.includes(c.idinfluencercommissionpayment))
    .reduce((sum, c) => sum + (c.commission_amount || 0), 0);

  // Sub-navigation items para o grupo Influenciadores
  const subNavItems = [
    { path: '/admin/influencers', label: 'Influenciadores', icon: <PersonAdd sx={{ fontSize: 18 }} /> },
    { path: '/admin/coupons', label: 'Cupons', icon: <LocalOffer sx={{ fontSize: 18 }} /> },
    { path: '/admin/commissions', label: 'Comissões', icon: <MonetizationOn sx={{ fontSize: 18 }} /> }
  ];

  return (
    <div className="commissions-page">
      {/* Sub Navigation */}
      <SubNavigation items={subNavItems} />

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <MonetizationOn sx={{ fontSize: 32 }} />
          <div>
            <h1>Comissões</h1>
            <p>Gerenciamento de pagamentos a influenciadores</p>
          </div>
        </div>
        
        {canEdit() && selectedIds.length > 0 && (
          <Button
            variant="primary"
            onClick={() => {
              setPaymentReference('');
              setPaymentNotes('');
              setPayError('');
              setShowBulkPayModal(true);
            }}
          >
            <Payments sx={{ fontSize: 20 }} />
            Pagar {selectedIds.length} selecionadas ({formatCurrency(selectedTotal)})
          </Button>
        )}
      </div>

      {/* Dashboard Cards */}
      {!dashboardLoading && dashboard && (
        <div className="dashboard-grid">
          <div className="dashboard-card pn-card pn-card--default">
            <div className="dashboard-card pn-card pn-card--default-icon pending">
              <MonetizationOn />
            </div>
            <div className="dashboard-card pn-card pn-card--default-content">
              <span className="dashboard-card pn-card pn-card--default-label">Pendente</span>
              <span className="dashboard-card pn-card pn-card--default-value">{formatCurrency(dashboard.overview?.pending_amount)}</span>
              <span className="dashboard-card pn-card pn-card--default-count">{dashboard.overview?.pending_count || 0} comissões</span>
            </div>
          </div>
          
          <div className="dashboard-card pn-card pn-card--default">
            <div className="dashboard-card pn-card pn-card--default-icon success">
              <CheckCircle />
            </div>
            <div className="dashboard-card pn-card pn-card--default-content">
              <span className="dashboard-card pn-card pn-card--default-label">Total Pago</span>
              <span className="dashboard-card pn-card pn-card--default-value">{formatCurrency(dashboard.overview?.paid_amount)}</span>
              <span className="dashboard-card pn-card pn-card--default-count">{dashboard.overview?.paid_count || 0} comissões</span>
            </div>
          </div>
          
          <div className="dashboard-card pn-card pn-card--default">
            <div className="dashboard-card pn-card pn-card--default-icon info">
              <TrendingUp />
            </div>
            <div className="dashboard-card pn-card pn-card--default-content">
              <span className="dashboard-card pn-card pn-card--default-label">Total Geral</span>
              <span className="dashboard-card pn-card pn-card--default-value">{formatCurrency(dashboard.overview?.total_amount)}</span>
              <span className="dashboard-card pn-card pn-card--default-count">{dashboard.overview?.total_payments || 0} pagamentos</span>
            </div>
          </div>
          
          <div className="dashboard-card pn-card pn-card--default">
            <div className="dashboard-card pn-card pn-card--default-icon">
              <Person />
            </div>
            <div className="dashboard-card pn-card pn-card--default-content">
              <span className="dashboard-card pn-card pn-card--default-label">Influenciadores Ativos</span>
              <span className="dashboard-card pn-card pn-card--default-value">{dashboard.active_commissions?.influencers || 0}</span>
              <span className="dashboard-card pn-card pn-card--default-count">{dashboard.active_commissions?.users || 0} usuários</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="admin-card pn-card pn-card--default filters-card">
        <div className="filters-row">
          <div className="search-box">
            <Search className="search-icon" />
            <input 
              type="text"
              placeholder="Buscar influenciador..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select 
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todos</option>
              <option value="pending">Pendente</option>
              <option value="paid">Pago</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Influenciador</label>
            <select 
              value={influencerFilter}
              onChange={(e) => {
                setInfluencerFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todos</option>
              {influencers.map(inf => (
                <option key={inf.idinfluencer} value={inf.idinfluencer}>
                  {inf.name} {inf.phone ? `(${inf.phone})` : ''}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>De</label>
            <input 
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>
          
          <div className="filter-group">
            <label>Até</label>
            <input 
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>
        </div>

        {/* Totals Summary */}
        {totals && (
          <div className="totals-summary">
            <div className="total-item">
              <span>Total Filtrado:</span>
              <strong>{formatCurrency(totals.total_amount)}</strong>
            </div>
            <div className="total-item pending">
              <span>Pendente:</span>
              <strong>{formatCurrency(totals.pending_amount)}</strong>
            </div>
            <div className="total-item success">
              <span>Pago:</span>
              <strong>{formatCurrency(totals.paid_amount)}</strong>
            </div>
          </div>
        )}
      </div>

      {/* Commissions Table */}
      <div className="admin-card pn-card pn-card--default">
        {error ? (
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>{error}</p>
            <Button variant="primary" onClick={loadCommissions}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    {canEdit() && (
                      <th style={{ width: '40px' }}>
                        <input 
                          type="checkbox"
                          checked={selectedIds.length > 0 && selectedIds.length === commissions.filter(c => c.status === 'pending').length}
                          onChange={handleSelectAll}
                        />
                      </th>
                    )}
                    <th>Influenciador</th>
                    <th>Cupom</th>
                    <th>Usuário</th>
                    <th>Mês Ref.</th>
                    <th>Valor Pago</th>
                    <th>Comissão</th>
                    <th>Status</th>
                    <th>Pago em</th>
                    {canEdit() && <th style={{ width: '100px' }}>Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i}>
                        {canEdit() && <td><Skeleton width={20} height={20} /></td>}
                        <td><Skeleton width={120} height={20} /></td>
                        <td><Skeleton width={80} height={20} /></td>
                        <td><Skeleton width={100} height={20} /></td>
                        <td><Skeleton width={60} height={20} /></td>
                        <td><Skeleton width={70} height={20} /></td>
                        <td><Skeleton width={70} height={20} /></td>
                        <td><Skeleton width={70} height={20} /></td>
                        <td><Skeleton width={80} height={20} /></td>
                        {canEdit() && <td><Skeleton width={60} height={30} /></td>}
                      </tr>
                    ))
                  ) : commissions.length === 0 ? (
                    <tr>
                      <td colSpan={canEdit() ? 10 : 9} className="empty-state">
                        <MonetizationOn sx={{ fontSize: 48, opacity: 0.3 }} />
                        <p>Nenhuma comissão encontrada</p>
                      </td>
                    </tr>
                  ) : (
                    commissions.map(commission => (
                      <tr key={commission.idinfluencercommissionpayment}>
                        {canEdit() && (
                          <td>
                            <input 
                              type="checkbox"
                              checked={selectedIds.includes(commission.idinfluencercommissionpayment)}
                              onChange={() => handleSelectOne(commission.idinfluencercommissionpayment)}
                              disabled={commission.status !== 'pending'}
                            />
                          </td>
                        )}
                        <td>
                          <div className="cell-with-icon">
                            <Person sx={{ fontSize: 16, opacity: 0.5 }} />
                            {commission.influencer_name}
                          </div>
                        </td>
                        <td>
                          <div className="cell-with-icon">
                            <LocalOffer sx={{ fontSize: 16, opacity: 0.5 }} />
                            <span className="coupon-code">{commission.coupon_code}</span>
                          </div>
                        </td>
                        <td>
                          <div className="user-cell">
                            <span>{commission.user_name}</span>
                            <span className="user-email">{commission.user_email}</span>
                          </div>
                        </td>
                        <td>
                          <div className="cell-with-icon">
                            <CalendarMonth sx={{ fontSize: 16, opacity: 0.5 }} />
                            {formatDate(commission.payment_month)}
                            <span className="month-number">Mês {commission.month_number}</span>
                          </div>
                        </td>
                        <td>{formatCurrency(commission.user_amount_paid)}</td>
                        <td className="commission-amount">
                          {formatCurrency(commission.commission_amount)}
                          <span className="commission-percent">({commission.commission_percent}%)</span>
                        </td>
                        <td>{getStatusBadge(commission.status)}</td>
                        <td>{commission.paid_at ? formatDate(commission.paid_at) : '-'}</td>
                        {canEdit() && (
                          <td>
                            {commission.status === 'pending' && (
                              <Button
                                variant="primary"
                                size="sm"
                                className="admin-btn-success"
                                onClick={() => openPayModal(commission)}
                                title="Marcar como pago"
                              >
                                <Payments sx={{ fontSize: 16 }} />
                                Pagar
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  Anterior
                </button>
                <span className="pagination-info">
                  Página {pagination.page} de {pagination.total_pages}
                </span>
                <button 
                  className="pagination-btn"
                  disabled={pagination.page === pagination.total_pages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FormModal
        open={showPayModal}
        onClose={() => setShowPayModal(false)}
        title="Confirmar Pagamento"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowPayModal(false)} disabled={payLoading}>
              Cancelar
            </Button>
            <Button variant="primary" className="admin-btn-success" onClick={handlePay} loading={payLoading}>
              Confirmar Pagamento
            </Button>
          </>
        }
      >
            <div className="modal-body">
              <div className="pay-summary">
                <div className="pay-info-row">
                  <span>Influenciador:</span>
                  <strong>{selectedCommission?.influencer_name}</strong>
                </div>
                <div className="pay-info-row">
                  <span>Cupom:</span>
                  <strong>{selectedCommission?.coupon_code}</strong>
                </div>
                <div className="pay-info-row">
                  <span>Usuário:</span>
                  <strong>{selectedCommission?.user_name}</strong>
                </div>
                <div className="pay-info-row highlight">
                  <span>Valor a pagar:</span>
                  <strong className="pay-amount">{formatCurrency(selectedCommission?.commission_amount)}</strong>
                </div>
              </div>

              {payError && (
                <div className="form-error">
                  <Warning sx={{ fontSize: 18 }} />
                  {payError}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Referência do Pagamento (opcional)</label>
                <div className="input-container">
                  <Receipt className="input-icon" />
                  <input 
                    type="text"
                    className="form-input"
                    placeholder="Ex: PIX-2025-01-001"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Observações (opcional)</label>
                <textarea 
                  className="form-input form-textarea"
                  placeholder="Observações sobre o pagamento..."
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  style={{ paddingLeft: '16px' }}
                />
              </div>
            </div>
      </FormModal>

      <FormModal
        open={showBulkPayModal}
        onClose={() => setShowBulkPayModal(false)}
        title="Pagamento em Lote"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowBulkPayModal(false)} disabled={bulkPayLoading}>
              Cancelar
            </Button>
            <Button variant="primary" className="admin-btn-success" onClick={handleBulkPay} loading={bulkPayLoading}>
              Pagar {selectedIds.length} comissões
            </Button>
          </>
        }
      >
            <div className="modal-body">
              <div className="pay-summary bulk">
                <div className="pay-info-row">
                  <span>Comissões selecionadas:</span>
                  <strong>{selectedIds.length}</strong>
                </div>
                <div className="pay-info-row highlight">
                  <span>Total a pagar:</span>
                  <strong className="pay-amount">{formatCurrency(selectedTotal)}</strong>
                </div>
              </div>

              {payError && (
                <div className="form-error">
                  <Warning sx={{ fontSize: 18 }} />
                  {payError}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Referência do Pagamento (opcional)</label>
                <div className="input-container">
                  <Receipt className="input-icon" />
                  <input 
                    type="text"
                    className="form-input"
                    placeholder="Ex: LOTE-JAN-2025"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Observações (opcional)</label>
                <textarea 
                  className="form-input form-textarea"
                  placeholder="Observações sobre o pagamento em lote..."
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  style={{ paddingLeft: '16px' }}
                />
              </div>
            </div>
      </FormModal>
    </div>
  );
};

export default CommissionsPage;

